// src/pages/event-content/ToursPage.tsx

import React, { useEffect, useMemo, useRef, useState } from "react";
import PageHeader from "../../components/ui/PageHeader";
import Card from "../../components/ui/Card";
import Button from "../../components/ui/Button";
import Field from "../../components/ui/Field";
import Input from "../../components/ui/Input";
import RichTextEditor from "../../components/ui/RichTextEditor";
import Switch from "../../components/ui/Switch";
import Select from "../../components/ui/Select";
import Modal from "../../components/ui/Modal";
import HallPricesModal from "../../components/hall-prices/HallPricesModal";
import { HallScheme } from "../../components/hall-schemes/HallSchemeEditorCanvas";
import {
    createTourRequest,
    getToursRequest,
    updateTourRequest,
    uploadTourCoverRequest,
    uploadTourPhotoRequest,
    deleteTourPhotoRequest,
    getOrganizersRequest,
    getCategoriesRequest,
    getTourCitiesRequest,
    getTourCityRequest,
    updateTourCityRequest,
    createTourCityRequest,
    getCountriesRequest,
    getCitiesRequest,
    getHallsRequest,
    getHallsByCityRequest,
    deleteTourCityRequest,
} from "../../services/api/event-content.api";
import {
    TourItem,
    OrganizerItem,
    EventCategoryItem,
    ToursResponseShape,
    TourCityItem,
    CountryItem,
    CityItem,
    HallItem,
} from "../../services/types/event-content.types";
import { Edit2, Trash2 } from "lucide-react";

interface TourFormState {
    nameRu: string;
    nameEn: string;
    nameDe: string;
    nameUa: string;
    slug: string;
    categoryId: string;
    organizerId: string;
    startAt: string;
    endAt: string;
    utmSource: string;
    shortDescRu: string;
    shortDescEn: string;
    shortDescDe: string;
    shortDescUa: string;
    fullDescRu: string;
    fullDescEn: string;
    fullDescDe: string;
    fullDescUa: string;
    isPublished: boolean;
    apiSalesAllowed: boolean;
    // На каких сайтах тур доступен (галочки).
    showOnMainSite: boolean;
    showOnUaSite: boolean;
    isSaleBlocked: boolean;
    blockedButtonTextRu: string;
    blockedButtonTextEn: string;
    blockedButtonTextDe: string;
    blockedButtonTextUa: string;
    youtubeIframe: string;
    price_from: string;
    isActive: boolean;
}

const initialFormState: TourFormState = {
    nameRu: "",
    nameEn: "",
    nameDe: "",
    nameUa: "",
    slug: "",
    categoryId: "",
    organizerId: "",
    startAt: "",
    endAt: "",
    utmSource: "",
    shortDescRu: "",
    shortDescEn: "",
    shortDescDe: "",
    shortDescUa: "",
    fullDescRu: "",
    fullDescEn: "",
    fullDescDe: "",
    fullDescUa: "",
    isPublished: false,
    apiSalesAllowed: false,
    showOnMainSite: true,
    showOnUaSite: false,
    isSaleBlocked: false,
    blockedButtonTextRu: "",
    blockedButtonTextEn: "",
    blockedButtonTextDe: "",
    blockedButtonTextUa: "",
    youtubeIframe: "",
    price_from: "0",
    isActive: true,
};

// Организатор Artist Production — для его туров в форме города дополнительно
// требуется ИД сеанса партнёра (используется для подтяжки данных зала по API).
const ARTIST_PRODUCTION_ORGANIZER_ID = "0468f6d9-e82b-44cd-af50-1505254674fc";

// Организатор NasheDE — для его туров в форме города требуется ИД мероприятия Nashe.
// Если ИД организатора известен — впишите его сюда; пока пусто, организатор
// определяется по названию (NasheDE / Nashe DE / nashe.de — без учёта регистра и пробелов).
const NASHE_DE_ORGANIZER_ID = "";
const isNasheDeOrganizerName = (name?: string | null): boolean =>
    (name ?? "").toLowerCase().replace(/[^a-z0-9]/g, "").startsWith("nashede");

const initialTourCityForm = {
    id: "",
    countryId: "",
    cityId: "",
    hallId: "",
    partnerSeanceId: "",
    // ИД мероприятия партнёра Nashe (необязательно, для любого организатора).
    nashePerformanceId: "",
    startAt: "",
    endAt: "",
    soon: false,
    isActive: true,
};

const normalizeResponse = (
    response: ToursResponseShape | TourItem[],
): {
    items: TourItem[];
    total: number;
} => {
    if (Array.isArray(response)) {
        return {
            items: response,
            total: response.length,
        };
    }

    if (response.items && Array.isArray(response.items)) {
        return {
            items: response.items,
            total: response.total_size ?? response.total ?? response.items.length,
        };
    }

    if (response.data && Array.isArray(response.data)) {
        return {
            items: response.data,
            total: response.total_size ?? response.total ?? response.data.length,
        };
    }

    return {
        items: [],
        total: 0,
    };
};

const toLocalApiDateTime = (value: string): string => {
    if (!value) {
        return "";
    }

    return `${value}:00`;
};

const normalizeDateTimeLocal = (value: string | null | undefined): string => {
    if (!value) {
        return "";
    }

    const parsed = new Date(value);

    if (Number.isNaN(parsed.getTime())) {
        return "";
    }

    const year = parsed.getFullYear();
    const month = String(parsed.getMonth() + 1).padStart(2, "0");
    const day = String(parsed.getDate()).padStart(2, "0");
    const hours = String(parsed.getHours()).padStart(2, "0");
    const minutes = String(parsed.getMinutes()).padStart(2, "0");

    return `${year}-${month}-${day}T${hours}:${minutes}`;
};

const ToursPage: React.FC = () => {
    const [items, setItems] = useState<TourItem[]>([]);
    const [selectedId, setSelectedId] = useState<string | null>(null);
    const [formState, setFormState] = useState<TourFormState>(initialFormState);

    const [coverImage, setCoverImage] = useState<string>("");
    const [photos, setPhotos] = useState<string[]>([]);

    const [searchValue, setSearchValue] = useState("");
    const [searchInputValue, setSearchInputValue] = useState("");
    const [page, setPage] = useState(1);
    const [pageSize] = useState(20);
    const [totalItems, setTotalItems] = useState(0);

    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [pageError, setPageError] = useState<string | null>(null);
    const [formError, setFormError] = useState<string | null>(null);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);

    const [isUploadingCover, setIsUploadingCover] = useState(false);
    const [isUploadingPhotos, setIsUploadingPhotos] = useState(false);
    const [isDeletingPhoto, setIsDeletingPhoto] = useState(false);

    const [organizers, setOrganizers] = useState<OrganizerItem[]>([]);
    const [categories, setCategories] = useState<EventCategoryItem[]>([]);

    const [tourCities, setTourCities] = useState<TourCityItem[]>([]);
    const [countries, setCountries] = useState<CountryItem[]>([]);
    const [cities, setCities] = useState<CityItem[]>([]);
    const [halls, setHalls] = useState<HallItem[]>([]);

    const [isGeoLoading, setIsGeoLoading] = useState(false);

    const [isTourCityModalOpen, setIsTourCityModalOpen] = useState(false);
    const [editingTourCityId, setEditingTourCityId] = useState<string | null>(null);
    const [isDeleteTourCityModalOpen, setIsDeleteTourCityModalOpen] = useState(false);
    const [tourCityToDelete, setTourCityToDelete] = useState<TourCityItem | null>(null);

    const [tourCityForm, setTourCityForm] = useState(initialTourCityForm);

    const [isCountriesLoading, setIsCountriesLoading] = useState(false);
    const [isCitiesLoading, setIsCitiesLoading] = useState(false);
    const [isHallsLoading, setIsHallsLoading] = useState(false);
    const [isTourCityModalDataLoading, setIsTourCityModalDataLoading] = useState(false);

    const [isHallPricesModalOpen, setIsHallPricesModalOpen] = useState(false);
    const [selectedPricesScheme, setSelectedPricesScheme] = useState<HallScheme | null>(null);
    const [selectedPricesHallName, setSelectedPricesHallName] = useState("");
    // ID сеанса у партнёра (Artist Production) для города тура, по которому открыта модалка цен
    // (null — обычный собственный тур, число — партнёрский).
    const [selectedPricesPartnerSeanceId, setSelectedPricesPartnerSeanceId] = useState<number | null>(null);

    const coverInputRef = useRef<HTMLInputElement | null>(null);
    const galleryInputRef = useRef<HTMLInputElement | null>(null);

    const totalPages = useMemo(
        () => Math.max(1, Math.ceil(totalItems / pageSize)),
        [totalItems, pageSize],
    );

    // ИД организатора текущего открытого тура (для условной логики Artist Production).
    const currentTourOrganizerId = useMemo(
        () => items.find((tour) => tour.id === selectedId)?.organizer_id ?? null,
        [items, selectedId],
    );
    const isArtistProductionOrganizer = currentTourOrganizerId === ARTIST_PRODUCTION_ORGANIZER_ID;
    // Организатор NasheDE: по ИД (если задан в NASHE_DE_ORGANIZER_ID) или по названию.
    const isNasheDeOrganizer = useMemo(() => {
        if (!currentTourOrganizerId) return false;
        if (NASHE_DE_ORGANIZER_ID && currentTourOrganizerId === NASHE_DE_ORGANIZER_ID) return true;
        const organizer = organizers.find((item) => String(item.id) === String(currentTourOrganizerId));
        return isNasheDeOrganizerName(organizer?.name);
    }, [currentTourOrganizerId, organizers]);

    const selectedHallOption = useMemo(() => {
        if (!tourCityForm.hallId) {
            return null;
        }

        const existingHall = halls.find(
            (item) => String(item.id) === String(tourCityForm.hallId),
        );

        if (existingHall) {
            return {
                value: String(existingHall.id),
                label:
                    (existingHall.internal_name?.trim() || null) ??
                    existingHall.name_ru ??
                    existingHall.name_en ??
                    existingHall.name_de ??
                    String(existingHall.id),
            };
        }

        return {
            value: String(tourCityForm.hallId),
            label: `ID: ${String(tourCityForm.hallId)}`,
        };
    }, [halls, tourCityForm.hallId]);

    const filteredHallOptions = useMemo(
        () =>
            halls
                .filter((item) => {
                    if (!tourCityForm.cityId) {
                        return false;
                    }

                    const hallCityId = item.city_id ?? item.city?.id ?? "";
                    if (String(hallCityId) !== String(tourCityForm.cityId)) {
                        return false;
                    }

                    // Для туров организатора Artist Production показываем только залы, созданные
                    // из данных партнёра (у них заполнен partner_seance_id) — чтобы админ не мог выбрать
                    // вручную схему зала без связи с сеансом.
                    if (isArtistProductionOrganizer && item.partner_seance_id == null) {
                        return false;
                    }

                    // Для туров NasheDE — только залы, импортированные из мероприятия Nashe.
                    if (isNasheDeOrganizer && item.nashe_performance_id == null) {
                        return false;
                    }

                    return true;
                })
                .map((item) => ({
                    value: String(item.id),
                    label:
                        (item.internal_name?.trim() || null) ??
                        item.name_ru ??
                        item.name_en ??
                        item.name_de ??
                        "",
                })),
        [halls, tourCityForm.cityId, isArtistProductionOrganizer, isNasheDeOrganizer],
    );

    const hallSelectOptions = useMemo(() => {
        const baseOptions = filteredHallOptions;

        if (
            selectedHallOption &&
            !baseOptions.some((item) => item.value === selectedHallOption.value)
        ) {
            return [selectedHallOption, ...baseOptions];
        }

        return baseOptions;
    }, [filteredHallOptions, selectedHallOption]);

    const countryOptions = useMemo(
        () =>
            countries.map((item) => ({
                value: String(item.id),
                label: item.name_ru ?? item.name_en ?? item.name_de ?? "Без названия",
            })),
        [countries],
    );

    const filteredCityOptions = useMemo(
        () =>
            cities
                .filter(
                    (item) =>
                        !tourCityForm.countryId ||
                        String(item.country_id) === String(tourCityForm.countryId),
                )
                .map((item) => ({
                    value: String(item.id),
                    label: item.name_ru ?? item.name_en ?? item.name_de ?? "Без названия",
                })),
        [cities, tourCityForm.countryId],
    );

    const organizerOptions = useMemo(
        () =>
            organizers.map((item) => ({
                value: item.id,
                label: item.name,
            })),
        [organizers],
    );

    const categoryOptions = useMemo(
        () =>
            categories.map((item) => ({
                value: item.id,
                label: item.name_ru,
            })),
        [categories],
    );

    const setFormStateFromItem = (item: TourItem) => {
        setFormState({
            nameRu: item.name_ru ?? "",
            nameEn: item.name_en ?? "",
            nameDe: item.name_de ?? "",
            nameUa: item.name_ua ?? "",
            slug: item.slug ?? "",
            categoryId: item.category_id ?? "",
            organizerId: item.organizer_id ?? "",
            startAt: normalizeDateTimeLocal(item.start_at),
            endAt: normalizeDateTimeLocal(item.end_at),
            utmSource: item.utm_source ?? "",
            shortDescRu: item.short_desc_ru ?? "",
            shortDescEn: item.short_desc_en ?? "",
            shortDescDe: item.short_desc_de ?? "",
            shortDescUa: item.short_desc_ua ?? "",
            fullDescRu: item.full_desc_ru ?? "",
            fullDescEn: item.full_desc_en ?? "",
            fullDescDe: item.full_desc_de ?? "",
            fullDescUa: item.full_desc_ua ?? "",
            isPublished: item.is_published,
            apiSalesAllowed: item.api_sales_allowed,
            showOnMainSite: item.show_on_main_site ?? true,
            showOnUaSite: item.show_on_ua_site ?? false,
            isSaleBlocked: item.is_sale_blocked,
            blockedButtonTextRu: item.blocked_button_text_ru ?? "",
            blockedButtonTextEn: item.blocked_button_text_en ?? "",
            blockedButtonTextDe: item.blocked_button_text_de ?? "",
            blockedButtonTextUa: item.blocked_button_text_ua ?? "",
            youtubeIframe: item.youtube_iframe ?? "",
            price_from: String(Number(item.price_from) ?? 0),
            isActive: item.is_active,
        });
        setCoverImage(item.cover_image ?? "");
        setPhotos(item.photos ?? []);
    };

    const resetForm = () => {
        setSelectedId(null);
        setFormState(initialFormState);
        setCoverImage("");
        setPhotos([]);
        setTourCities([]);
        setFormError(null);
        setSuccessMessage(null);
    };

    const loadRefs = async () => {
        try {
            const [organizersResp, categoriesResp] = await Promise.all([
                getOrganizersRequest(),
                getCategoriesRequest(),
            ]);

            setOrganizers(organizersResp);
            setCategories(categoriesResp);
        } catch {
            // справочники страницы не критичны
        }
    };

    const loadGeoRefs = async () => {
        setIsGeoLoading(true);

        try {
            const [countriesResp, citiesResp, hallsResp] = await Promise.all([
                getCountriesRequest(),
                getCitiesRequest(),
                getHallsRequest({
                    page: 1,
                    size: 500,
                }),
            ]);

            setCountries(Array.isArray(countriesResp) ? countriesResp : []);
            setCities(Array.isArray(citiesResp) ? citiesResp : []);

            const hallsItems = Array.isArray(hallsResp)
                ? hallsResp
                : hallsResp.items && Array.isArray(hallsResp.items)
                    ? hallsResp.items
                    : hallsResp.data && Array.isArray(hallsResp.data)
                        ? hallsResp.data
                        : [];

            setHalls(hallsItems);
        } catch (error) {
            setFormError(
                error instanceof Error
                    ? error.message
                    : "Не удалось загрузить страны, города и залы.",
            );
        } finally {
            setIsGeoLoading(false);
        }
    };

    const loadData = async () => {
        setIsLoading(true);
        setPageError(null);

        try {
            const response = await getToursRequest({
                page,
                size: pageSize,
                search: searchValue.trim() || undefined,
            });

            const normalized = normalizeResponse(response);

            setItems(normalized.items);
            setTotalItems(normalized.total);

            if (selectedId) {
                const selectedItem = normalized.items.find((item) => item.id === selectedId);

                if (!selectedItem) {
                    setSelectedId(null);
                    setFormState(initialFormState);
                    setCoverImage("");
                    setPhotos([]);
                } else {
                    setFormStateFromItem(selectedItem);
                }
            }
        } catch (error) {
            setPageError(error instanceof Error ? error.message : "Не удалось загрузить туры.");
        } finally {
            setIsLoading(false);
        }
    };

    const loadTourCities = async (tourId: string) => {
        try {
            const response = await getTourCitiesRequest(tourId);
            setTourCities(response.filter((item) => !item.is_deleted));
        } catch (error) {
            setFormError(error instanceof Error ? error.message : "Не удалось загрузить города тура");
        }
    };

    useEffect(() => {
        const preventWindowDrop = (event: DragEvent) => {
            event.preventDefault();
        };

        window.addEventListener("dragover", preventWindowDrop);
        window.addEventListener("drop", preventWindowDrop);

        return () => {
            window.removeEventListener("dragover", preventWindowDrop);
            window.removeEventListener("drop", preventWindowDrop);
        };
    }, []);

    useEffect(() => {
        if (!selectedId) {
            setTourCities([]);
            return;
        }

        void loadTourCities(selectedId);
    }, [selectedId]);

    useEffect(() => {
        void loadRefs();
    }, []);

    useEffect(() => {
        void loadData();
    }, [page, pageSize, searchValue]);

    const handleSelect = (item: TourItem) => {
        setSelectedId(item.id);
        setFormStateFromItem(item);
        setFormError(null);
        setSuccessMessage(null);
    };

    const handleSearchSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setPage(1);
        setSearchValue(searchInputValue);
    };

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setFormError(null);
        setSuccessMessage(null);

        if (!formState.nameRu.trim() || !formState.nameEn.trim() || !formState.nameDe.trim()) {
            setFormError("Название тура должно быть заполнено на трёх языках.");
            return;
        }

        if (!formState.startAt || !formState.endAt) {
            setFormError("Дата начала и конца тура обязательна.");
            return;
        }

        setIsSaving(true);

        try {
            const payloadBase = {
                name_ru: formState.nameRu.trim(),
                name_en: formState.nameEn.trim(),
                name_de: formState.nameDe.trim(),
                name_ua: formState.nameUa.trim() || null,
                slug: formState.slug || null,
                category_id: formState.categoryId || null,
                organizer_id: formState.organizerId || null,
                start_at: toLocalApiDateTime(formState.startAt),
                end_at: toLocalApiDateTime(formState.endAt),
                utm_source: formState.utmSource.trim() || null,
                short_desc_ru: formState.shortDescRu.trim() || null,
                short_desc_en: formState.shortDescEn.trim() || null,
                short_desc_de: formState.shortDescDe.trim() || null,
                short_desc_ua: formState.shortDescUa.trim() || null,
                full_desc_ru: formState.fullDescRu.trim() || null,
                full_desc_en: formState.fullDescEn.trim() || null,
                full_desc_de: formState.fullDescDe.trim() || null,
                full_desc_ua: formState.fullDescUa.trim() || null,
                is_published: formState.isPublished,
                api_sales_allowed: formState.apiSalesAllowed,
                show_on_main_site: formState.showOnMainSite,
                show_on_ua_site: formState.showOnUaSite,
                is_sale_blocked: formState.isSaleBlocked,
                blocked_button_text_ru: formState.blockedButtonTextRu.trim() || null,
                blocked_button_text_en: formState.blockedButtonTextEn.trim() || null,
                blocked_button_text_de: formState.blockedButtonTextDe.trim() || null,
                blocked_button_text_ua: formState.blockedButtonTextUa.trim() || null,
                cover_image: coverImage || null,
                photos,
                youtube_iframe: formState.youtubeIframe.trim() || null,
                price_from: Number(formState.price_from.trim()) ? Number(formState.price_from.trim()) : null,
                is_active: formState.isActive,
            };

            const savedItem = selectedId
                ? await updateTourRequest(selectedId, payloadBase)
                : await createTourRequest(payloadBase);

            setSuccessMessage(selectedId ? "Тур обновлён." : "Тур создан.");

            if (selectedId) {
                setItems((prev) => prev.map((item) => (item.id === savedItem.id ? savedItem : item)));
                handleSelect(savedItem);
            } else {
                setPage(1);
                setSearchValue("");
                setSearchInputValue("");
                setSelectedId(savedItem.id);
                await loadData();
            }
        } catch (error) {
            setFormError(error instanceof Error ? error.message : "Ошибка сохранения тура");
        } finally {
            setIsSaving(false);
        }
    };

    const handleUploadCover = async (file: File | null) => {
        if (!file || !selectedId) {
            return;
        }

        setIsUploadingCover(true);
        setFormError(null);
        setSuccessMessage(null);

        try {
            const updated = await uploadTourCoverRequest(selectedId, file);
            setCoverImage(updated.cover_image ?? "");
            setItems((prev) =>
                prev.map((item) => (item.id === updated.id ? updated : item)),
            );
            setSuccessMessage("Обложка успешно загружена.");
        } catch (error) {
            setFormError(error instanceof Error ? error.message : "Не удалось загрузить обложку");
        } finally {
            setIsUploadingCover(false);
        }
    };

    const handleUploadPhotos = async (files: FileList | null) => {
        if (!files || !selectedId) {
            return;
        }

        setIsUploadingPhotos(true);
        setFormError(null);
        setSuccessMessage(null);

        try {
            let nextPhotos = [...photos];

            for (const file of Array.from(files)) {
                const updated = await uploadTourPhotoRequest(selectedId, file);
                nextPhotos = updated.photos ?? nextPhotos;
            }

            setPhotos(nextPhotos);

            setItems((prev) =>
                prev.map((item) =>
                    item.id === selectedId
                        ? {
                            ...item,
                            photos: nextPhotos,
                        }
                        : item,
                ),
            );

            setSuccessMessage("Фотографии успешно загружены.");
        } catch (error) {
            setFormError(error instanceof Error ? error.message : "Не удалось загрузить фотографии");
        } finally {
            setIsUploadingPhotos(false);
        }
    };

    const handleCoverDrop = (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        event.stopPropagation();

        const file = event.dataTransfer.files?.[0] ?? null;
        void handleUploadCover(file);
    };

    const handleGalleryDrop = (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        event.stopPropagation();

        void handleUploadPhotos(event.dataTransfer.files);
    };

    const handleDeletePhoto = async (photoUrl: string) => {
        if (!selectedId) {
            return;
        }

        setIsDeletingPhoto(true);
        setFormError(null);
        setSuccessMessage(null);

        try {
            const updated = await deleteTourPhotoRequest(selectedId, photoUrl);
            const nextPhotos = updated.photos ?? photos.filter((x) => x !== photoUrl);

            setPhotos(nextPhotos);
            setItems((prev) =>
                prev.map((item) =>
                    item.id === updated.id
                        ? {
                            ...item,
                            photos: nextPhotos,
                        }
                        : item,
                ),
            );

            setSuccessMessage("Фотография удалена.");
        } catch (error) {
            setFormError(error instanceof Error ? error.message : "Не удалось удалить фотографию");
        } finally {
            setIsDeletingPhoto(false);
        }
    };

    const movePhoto = async (from: number, to: number) => {
        if (!selectedId || to < 0 || to >= photos.length) {
            return;
        }

        const next = [...photos];
        const [moved] = next.splice(from, 1);
        next.splice(to, 0, moved);

        setPhotos(next);

        try {
            await updateTourRequest(selectedId, { photos: next });
            setItems((prev) =>
                prev.map((item) =>
                    item.id === selectedId
                        ? {
                            ...item,
                            photos: next,
                        }
                        : item,
                ),
            );
        } catch (error) {
            setFormError(error instanceof Error ? error.message : "Не удалось обновить сортировку фото");
        }
    };

    const handleConfirmDeleteTourCity = async () => {
        if (!tourCityToDelete) return;

        try {
            await deleteTourCityRequest(tourCityToDelete.id);

            setTourCities((prev) =>
                prev.filter((item) => item.id !== tourCityToDelete.id),
            );

            setIsDeleteTourCityModalOpen(false);
            setTourCityToDelete(null);
        } catch (error) {
            setFormError(error instanceof Error ? error.message : "Не удалось удалить город тура.");
        }
    };

    const handleSubmitTourCity = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        if (!selectedId) {
            setFormError("Сначала выберите тур.");
            return;
        }

        setFormError(null);
        setSuccessMessage(null);

        if (!tourCityForm.countryId) {
            setFormError("Выберите страну.");
            return;
        }

        if (!tourCityForm.cityId) {
            setFormError("Выберите город.");
            return;
        }

        if (!tourCityForm.startAt) {
            setFormError("Укажите дату и время начала.");
            return;
        }

        if (!tourCityForm.endAt) {
            setFormError("Укажите дату и время окончания.");
            return;
        }

        if (isArtistProductionOrganizer && !tourCityForm.partnerSeanceId.trim()) {
            setFormError("Укажите ИД сеанса Artist Production.");
            return;
        }

        if (isNasheDeOrganizer && !tourCityForm.nashePerformanceId.trim()) {
            setFormError("Укажите ИД мероприятия NasheDE.");
            return;
        }

        const startAtIso = toLocalApiDateTime(tourCityForm.startAt);
        const endAtIso = toLocalApiDateTime(tourCityForm.endAt);

        if (new Date(startAtIso).getTime() > new Date(endAtIso).getTime()) {
            setFormError("Дата окончания не может быть раньше даты начала.");
            return;
        }

        setIsSaving(true);

        try {
            const payload = {
                country_id: tourCityForm.countryId,
                city_id: tourCityForm.cityId,
                hall_id: tourCityForm.hallId || null,
                partner_seance_id:
                    isArtistProductionOrganizer && tourCityForm.partnerSeanceId.trim()
                        ? Number(tourCityForm.partnerSeanceId)
                        : null,
                // ИД мероприятия Nashe — только для туров NasheDE (null отвязывает от Nashe).
                nashe_performance_id:
                    isNasheDeOrganizer && tourCityForm.nashePerformanceId.trim()
                        ? Number(tourCityForm.nashePerformanceId)
                        : null,
                start_at: startAtIso,
                end_at: endAtIso,
                soon: tourCityForm.soon,
                is_active: tourCityForm.isActive,
            };

            if (editingTourCityId) {
                const updated = await updateTourCityRequest(editingTourCityId, payload);

                setTourCities((prev) =>
                    prev.map((item) => (item.id === updated.id ? updated : item)),
                );

                setSuccessMessage("Город тура успешно обновлён.");
            } else {
                const created = await createTourCityRequest({
                    ...payload,
                    tour_id: selectedId,
                });

                setTourCities((prev) => [created, ...prev]);
                setSuccessMessage("Город тура успешно добавлен.");
            }

            setIsTourCityModalOpen(false);
            setEditingTourCityId(null);
            setTourCityForm(initialTourCityForm);

            await loadTourCities(selectedId);
        } catch (error) {
            setFormError(
                error instanceof Error ? error.message : "Не удалось сохранить город тура.",
            );
        } finally {
            setIsSaving(false);
        }
    };

    const loadCountries = async () => {
        setIsCountriesLoading(true);

        try {
            const response = await getCountriesRequest();
            setCountries(Array.isArray(response) ? response : []);
        } catch (error) {
            setFormError(error instanceof Error ? error.message : "Не удалось загрузить страны.");
        } finally {
            setIsCountriesLoading(false);
        }
    };

    const loadCitiesByCountry = async (countryId: string) => {
        if (!countryId) {
            setCities([]);
            setHalls([]);
            return;
        }

        setIsCitiesLoading(true);

        try {
            const response = await getCitiesRequest(countryId);
            setCities(Array.isArray(response) ? response : []);
            setHalls([]);
        } catch (error) {
            setFormError(error instanceof Error ? error.message : "Не удалось загрузить города.");
        } finally {
            setIsCitiesLoading(false);
        }
    };

    const loadHallsByCity = async (cityName: string) => {
        if (!cityName) {
            setHalls([]);
            return;
        }

        setIsHallsLoading(true);

        try {
            const response = await getHallsByCityRequest({
                page: 1,
                size: 100,
                search: cityName,
            });

            const hallsItems = Array.isArray(response)
                ? response
                : Array.isArray(response.items)
                    ? response.items
                    : Array.isArray(response.data)
                        ? response.data
                        : [];

            setHalls(hallsItems);
        } catch (error) {
            setFormError(error instanceof Error ? error.message : "Не удалось загрузить залы.");
            setHalls([]);
        } finally {
            setIsHallsLoading(false);
        }
    };

    const handleOpenCreateTourCityModal = async () => {
        setEditingTourCityId(null);
        setTourCityForm(initialTourCityForm);
        setCities([]);
        setHalls([]);
        setFormError(null);
        setIsTourCityModalOpen(true);
        setIsTourCityModalDataLoading(true);

        try {
            await loadCountries();
        } finally {
            setIsTourCityModalDataLoading(false);
        }
    };

    const handleEditTourCity = async (tourCityId: string) => {
        setFormError(null);
        setEditingTourCityId(tourCityId);
        setIsTourCityModalOpen(true);
        setIsTourCityModalDataLoading(true);

        try {
            const item = await getTourCityRequest(tourCityId);

            const nextForm = {
                id: item.id ? String(item.id) : "",
                countryId: item.country_id ? String(item.country_id) : "",
                cityId: item.city_id ? String(item.city_id) : "",
                hallId: item.hall_id ? String(item.hall_id) : "",
                partnerSeanceId:
                    item.partner_seance_id !== null && item.partner_seance_id !== undefined
                        ? String(item.partner_seance_id)
                        : "",
                nashePerformanceId:
                    item.nashe_performance_id !== null && item.nashe_performance_id !== undefined
                        ? String(item.nashe_performance_id)
                        : "",
                startAt: normalizeDateTimeLocal(item.start_at),
                endAt: normalizeDateTimeLocal(item.end_at),
                soon: Boolean(item.soon),
                isActive: item.is_active,
            };

            setEditingTourCityId(item.id);
            setTourCityForm(nextForm);

            if (!countries.length) {
                const countriesResponse = await getCountriesRequest();
                setCountries(Array.isArray(countriesResponse) ? countriesResponse : []);
            }

            let nextCities: CityItem[] = [];

            if (nextForm.countryId) {
                setIsCitiesLoading(true);
                try {
                    const citiesResponse = await getCitiesRequest(nextForm.countryId);
                    nextCities = Array.isArray(citiesResponse) ? citiesResponse : [];
                    setCities(nextCities);
                } finally {
                    setIsCitiesLoading(false);
                }
            } else {
                setCities([]);
            }

            if (nextForm.cityId) {
                const selectedCity = nextCities.find(
                    (city) => String(city.id) === String(nextForm.cityId),
                );

                const cityName =
                    selectedCity?.name_ru ??
                    selectedCity?.name_en ??
                    selectedCity?.name_de ??
                    item.city?.name_ru ??
                    item.city?.name_en ??
                    item.city?.name_de ??
                    "";

                if (cityName) {
                    await loadHallsByCity(cityName);
                } else {
                    setHalls([]);
                }
            } else {
                setHalls([]);
            }
        } catch (error) {
            setFormError(
                error instanceof Error
                    ? error.message
                    : "Не удалось загрузить данные города тура.",
            );
        } finally {
            setIsTourCityModalDataLoading(false);
        }
    };

    const handleOpenHallPricesModal = (item: TourCityItem) => {
        if (!item.hall?.scheme) {
            setFormError("У этого зала пока нет схемы. Сначала создайте или сохраните схему зала.");
            return;
        }

        setTourCityForm((prev) => ({
            ...prev,
            id: item.id,
        }));
        setFormError(null);
        setSuccessMessage(null);
        setSelectedPricesScheme(item.hall.scheme);
        setSelectedPricesHallName(
            (item.hall?.internal_name?.trim() || null) ??
                item.hall?.name_ru ??
                item.hall?.name_en ??
                item.hall?.name_de ??
                "",
        );
        // Партнёрский режим цен (только просмотр, заказ через партнёра) — и для
        // Artist Production, и для Nashe.
        setSelectedPricesPartnerSeanceId(item.partner_seance_id ?? item.nashe_performance_id ?? null);
        setIsHallPricesModalOpen(true);
    };

    return (
        <div className="page-content">
            <PageHeader
                title="Туры"
                description="Справочник туров, даты, описания, публикация, обложка и фотогалерея."
                actions={<Button onClick={resetForm}>Новый тур</Button>}
            />

            <section className="content-split artist-layout">
                <Card className="content-list-card">
                    <div className="section-toolbar">
                        <div>
                            <h3 className="card-title">Список туров</h3>
                            <p className="card-text">
                                Туры с датами, городами, статусами публикации и активностью.
                            </p>
                        </div>

                        <form className="section-toolbar-controls" onSubmit={handleSearchSubmit}>
                            <Input
                                type="text"
                                placeholder="Поиск по названию"
                                value={searchInputValue}
                                onChange={(event) => setSearchInputValue(event.target.value)}
                            />
                        </form>
                    </div>

                    {pageError ? (
                        <div className="form-global-error">{pageError}</div>
                    ) : isLoading ? (
                        <div className="table-skeleton">
                            <div className="table-skeleton-row" />
                            <div className="table-skeleton-row" />
                            <div className="table-skeleton-row" />
                        </div>
                    ) : items.length === 0 ? (
                        <div className="empty-block">
                            <h3 className="empty-block-title">Туры не найдены</h3>
                            <p className="empty-block-text">
                                Создайте первый тур или измените поисковый запрос.
                            </p>
                        </div>
                    ) : (
                        <>
                            <div className="artist-list-grid">
                                {items.map((item) => {
                                    const startDate = item.start_at
                                        ? new Date(item.start_at).toLocaleDateString("ru-RU")
                                        : "—";
                                    const endDate = item.end_at
                                        ? new Date(item.end_at).toLocaleDateString("ru-RU")
                                        : "—";

                                    return (
                                        <button
                                            key={item.id}
                                            type="button"
                                            className={`artist-list-item artist-list-item-compact ${
                                                item.id === selectedId ? "artist-list-item-active" : ""
                                            }`}
                                            onClick={() => handleSelect(item)}
                                        >
                                            <div className="tour-list-content">
                                                {item.cover_image ? (
                                                    <img
                                                        src={item.cover_image}
                                                        alt={item.name_ru}
                                                        className="tour-list-preview"
                                                    />
                                                ) : (
                                                    <div className="tour-list-preview tour-list-preview-placeholder">
                                                        Нет фото
                                                    </div>
                                                )}

                                                <div className="tour-list-meta">
                                                    <span className="artist-list-title">{item.name_ru}</span>
                                                    <span className="artist-list-subtitle">
                                                        {startDate} — {endDate}
                                                    </span>
                                                    <span className="tour-sites-badges">
                                                        {item.show_on_main_site !== false ? (
                                                            <span className="tour-site-badge" title="Доступен на основном сайте">Основной</span>
                                                        ) : null}
                                                        {item.show_on_ua_site ? (
                                                            <span className="tour-site-badge tour-site-badge-ua" title="Доступен на втором сайте">UA</span>
                                                        ) : null}
                                                        {item.show_on_main_site === false && !item.show_on_ua_site ? (
                                                            <span className="tour-site-badge tour-site-badge-none">Скрыт на сайтах</span>
                                                        ) : null}
                                                    </span>
                                                </div>
                                            </div>

                                            <div className="inline-actions">
                                                <span
                                                    className={`status-dot ${
                                                        item.is_active
                                                            ? "status-dot-active"
                                                            : "status-dot-inactive"
                                                    }`}
                                                />
                                            </div>
                                        </button>
                                    );
                                })}
                            </div>

                            <div className="pagination-row">
                                <Button
                                    type="button"
                                    variant="secondary"
                                    disabled={page <= 1}
                                    onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                                >
                                    Назад
                                </Button>

                                <span className="pagination-text">
                                    Страница {page} из {totalPages}
                                </span>

                                <Button
                                    type="button"
                                    variant="secondary"
                                    disabled={page >= totalPages}
                                    onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
                                >
                                    Вперёд
                                </Button>
                            </div>
                        </>
                    )}
                </Card>

                <Card className="content-form-card">
                    <div className="editor-head">
                        <div>
                            <h3 className="card-title">
                                {selectedId ? "Редактирование тура" : "Новый тур"}
                            </h3>
                            <p className="card-text">
                                Управление основными данными тура, обложкой и фотогалереей.
                            </p>
                        </div>
                    </div>

                    <form className="editor-form" onSubmit={handleSubmit}>
                        {selectedId ? (
                            <Card className="hall-gallery-card">
                                <div className="editor-head">
                                    <div>
                                        <h3 className="card-title">Города тура</h3>
                                        <p className="card-text">
                                            Список городов и дат проведения мероприятий в рамках тура.
                                        </p>
                                    </div>

                                    <Button
                                        type="button"
                                        onClick={() => {
                                            void handleOpenCreateTourCityModal();
                                        }}
                                    >
                                        Добавить город
                                    </Button>
                                </div>

                                {tourCities.length === 0 ? (
                                    <div className="empty-block">
                                        <h3 className="empty-block-title">Города пока не добавлены</h3>
                                        <p className="empty-block-text">
                                            Добавьте первую остановку тура через модальное окно.
                                        </p>
                                    </div>
                                ) : (
                                    <div className="artist-list-grid">
                                        {tourCities.map((item) => (
                                            <div key={item.id} className="artist-list-item artist-list-item-compact">
                                                <div className="artist-list-content">
                                                    <span className="artist-list-title">
                                                        {item.city?.name_ru ?? "Город не указан"}
                                                    </span>
                                                    <span className="artist-list-subtitle">
                                                        {new Date(item.start_at).toLocaleString("ru-RU")}
                                                    </span>
                                                    {item.soon ?
                                                        <span className="artist-list-subtitle" style={{color: 'rgb(118 114 252)'}}>
                                                            Скоро в продаже
                                                        </span>
                                                        :
                                                        <span className="artist-list-subtitle" style={{color: '#10b981'}}>
                                                            Билеты в продаже
                                                        </span>
                                                    }
                                                </div>

                                                <div className="inline-actions">
                                                    <Button
                                                        type="button"
                                                        variant="ghost"
                                                        onClick={() => void handleEditTourCity(item.id)}
                                                    >
                                                        <Edit2 size={16} />
                                                    </Button>

                                                    <Button
                                                        type="button"
                                                        variant="ghost"
                                                        onClick={(event) => {
                                                            event.stopPropagation();
                                                            handleOpenHallPricesModal(item);
                                                        }}
                                                        disabled={!item.hall?.scheme}
                                                    >
                                                        Цены на билеты
                                                    </Button>

                                                    <Button
                                                        type="button"
                                                        variant="ghost"
                                                        onClick={() => {
                                                            setTourCityToDelete(item);
                                                            setIsDeleteTourCityModalOpen(true);
                                                        }}
                                                    >
                                                        <Trash2 size={16} />
                                                    </Button>

                                                    <div className="inline-actions">
                                                        <span
                                                            className={`status-dot ${
                                                                item.is_active
                                                                    ? "status-dot-active"
                                                                    : "status-dot-inactive"
                                                            }`}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </Card>
                        ) : null}

                        <div className="form-grid form-grid-two-columns">
                            <Field label="Название (RU)" htmlFor="tour-name-ru">
                                <Input
                                    id="tour-name-ru"
                                    value={formState.nameRu}
                                    onChange={(event) =>
                                        setFormState((prev) => ({ ...prev, nameRu: event.target.value }))
                                    }
                                />
                            </Field>

                            <Field label="Название (EN)" htmlFor="tour-name-en">
                                <Input
                                    id="tour-name-en"
                                    value={formState.nameEn}
                                    onChange={(event) =>
                                        setFormState((prev) => ({ ...prev, nameEn: event.target.value }))
                                    }
                                />
                            </Field>

                            <Field label="Название (DE)" htmlFor="tour-name-de">
                                <Input
                                    id="tour-name-de"
                                    value={formState.nameDe}
                                    onChange={(event) =>
                                        setFormState((prev) => ({ ...prev, nameDe: event.target.value }))
                                    }
                                />
                            </Field>

                            <Field label="Название (UA)" htmlFor="tour-name-ua">
                                <Input
                                    id="tour-name-ua"
                                    value={formState.nameUa}
                                    onChange={(event) =>
                                        setFormState((prev) => ({ ...prev, nameUa: event.target.value }))
                                    }
                                />
                            </Field>
                            <Field label="Slug (ЧПУ)" htmlFor="tour-slug">
                                <Input
                                    id="tour-slug"
                                    value={formState.slug}
                                    onChange={(event) =>
                                        setFormState((prev) => ({ ...prev, slug: event.target.value }))
                                    }
                                />
                            </Field>

                            <Field label="Категория" htmlFor="tour-category">
                                <Select
                                    id="tour-category"
                                    options={categoryOptions}
                                    value={formState.categoryId}
                                    onChange={(event) =>
                                        setFormState((prev) => ({
                                            ...prev,
                                            categoryId: event.target.value,
                                        }))
                                    }
                                    placeholder="Выберите категорию"
                                />
                            </Field>

                            <Field label="Организатор" htmlFor="tour-organizer">
                                <Select
                                    id="tour-organizer"
                                    options={organizerOptions}
                                    value={formState.organizerId}
                                    onChange={(event) =>
                                        setFormState((prev) => ({
                                            ...prev,
                                            organizerId: event.target.value,
                                        }))
                                    }
                                    placeholder="Выберите организатора"
                                />
                            </Field>

                            <Field label="Дата начала" htmlFor="tour-start-at">
                                <Input
                                    id="tour-start-at"
                                    type="datetime-local"
                                    value={formState.startAt}
                                    className={'white-calendar-icon'}
                                    onChange={(event) =>
                                        setFormState((prev) => ({
                                            ...prev,
                                            startAt: event.target.value,
                                        }))
                                    }
                                />
                            </Field>

                            <Field label="Дата окончания" htmlFor="tour-end-at">
                                <Input
                                    id="tour-end-at"
                                    type="datetime-local"
                                    value={formState.endAt}
                                    className={'white-calendar-icon'}
                                    onChange={(event) =>
                                        setFormState((prev) => ({
                                            ...prev,
                                            endAt: event.target.value,
                                        }))
                                    }
                                />
                            </Field>

                            <Field label="UTM source" htmlFor="tour-utm-source">
                                <Input
                                    id="tour-utm-source"
                                    value={formState.utmSource}
                                    onChange={(event) =>
                                        setFormState((prev) => ({
                                            ...prev,
                                            utmSource: event.target.value,
                                        }))
                                    }
                                />
                            </Field>

                            <Field label="Youtube iframe" htmlFor="tour-youtube">
                                <Input
                                    id="tour-youtube"
                                    value={formState.youtubeIframe}
                                    onChange={(event) =>
                                        setFormState((prev) => ({
                                            ...prev,
                                            youtubeIframe: event.target.value,
                                        }))
                                    }
                                />
                            </Field>

                            <Field label="Цена от €" htmlFor="tour-pricefrom">
                                <Input
                                    id="tour-pricefrom"
                                    type="number"
                                    value={Number(formState.price_from)}
                                    onChange={(event) =>
                                        setFormState((prev) => ({
                                            ...prev,
                                            price_from: String(Number(event.target.value.trim())),
                                        }))
                                    }
                                />
                            </Field>
                        </div>

                        <Field label="Краткое описание (RU)" htmlFor="tour-short-ru">
                            <Input
                                id="tour-short-ru"
                                value={formState.shortDescRu}
                                onChange={(event) =>
                                    setFormState((prev) => ({
                                        ...prev,
                                        shortDescRu: event.target.value,
                                    }))
                                }
                            />
                        </Field>

                        <Field label="Краткое описание (EN)" htmlFor="tour-short-en">
                            <Input
                                id="tour-short-en"
                                value={formState.shortDescEn}
                                onChange={(event) =>
                                    setFormState((prev) => ({
                                        ...prev,
                                        shortDescEn: event.target.value,
                                    }))
                                }
                            />
                        </Field>

                        <Field label="Краткое описание (DE)" htmlFor="tour-short-de">
                            <Input
                                id="tour-short-de"
                                value={formState.shortDescDe}
                                onChange={(event) =>
                                    setFormState((prev) => ({
                                        ...prev,
                                        shortDescDe: event.target.value,
                                    }))
                                }
                            />
                        </Field>

                        <Field label="Краткое описание (UA)" htmlFor="tour-short-ua">
                            <Input
                                id="tour-short-ua"
                                value={formState.shortDescUa}
                                onChange={(event) =>
                                    setFormState((prev) => ({
                                        ...prev,
                                        shortDescUa: event.target.value,
                                    }))
                                }
                            />
                        </Field>

                        <RichTextEditor
                            label="Полное описание (RU)"
                            value={formState.fullDescRu}
                            onChange={(value) =>
                                setFormState((prev) => ({ ...prev, fullDescRu: value }))
                            }
                        />

                        <RichTextEditor
                            label="Полное описание (EN)"
                            value={formState.fullDescEn}
                            onChange={(value) =>
                                setFormState((prev) => ({ ...prev, fullDescEn: value }))
                            }
                        />

                        <RichTextEditor
                            label="Полное описание (DE)"
                            value={formState.fullDescDe}
                            onChange={(value) =>
                                setFormState((prev) => ({ ...prev, fullDescDe: value }))
                            }
                        />

                        <RichTextEditor
                            label="Полное описание (UA)"
                            value={formState.fullDescUa}
                            onChange={(value) =>
                                setFormState((prev) => ({ ...prev, fullDescUa: value }))
                            }
                        />

                        <div className="form-grid form-grid-two-columns">
                            <Field label="Текст кнопки при блокировке (RU)" htmlFor="tour-blocked-ru">
                                <Input
                                    id="tour-blocked-ru"
                                    value={formState.blockedButtonTextRu}
                                    onChange={(event) =>
                                        setFormState((prev) => ({
                                            ...prev,
                                            blockedButtonTextRu: event.target.value,
                                        }))
                                    }
                                />
                            </Field>

                            <Field label="Текст кнопки при блокировке (EN)" htmlFor="tour-blocked-en">
                                <Input
                                    id="tour-blocked-en"
                                    value={formState.blockedButtonTextEn}
                                    onChange={(event) =>
                                        setFormState((prev) => ({
                                            ...prev,
                                            blockedButtonTextEn: event.target.value,
                                        }))
                                    }
                                />
                            </Field>

                            <Field label="Текст кнопки при блокировке (DE)" htmlFor="tour-blocked-de">
                                <Input
                                    id="tour-blocked-de"
                                    value={formState.blockedButtonTextDe}
                                    onChange={(event) =>
                                        setFormState((prev) => ({
                                            ...prev,
                                            blockedButtonTextDe: event.target.value,
                                        }))
                                    }
                                />
                            </Field>

                            <Field label="Текст кнопки при блокировке (UA)" htmlFor="tour-blocked-ua">
                                <Input
                                    id="tour-blocked-ua"
                                    value={formState.blockedButtonTextUa}
                                    onChange={(event) =>
                                        setFormState((prev) => ({
                                            ...prev,
                                            blockedButtonTextUa: event.target.value,
                                        }))
                                    }
                                />
                            </Field>
                        </div>

                        <div className="form-switch-row">
                            <Switch
                                checked={formState.isPublished}
                                onChange={(checked) =>
                                    setFormState((prev) => ({ ...prev, isPublished: checked }))
                                }
                                label={formState.isPublished ? "Опубликован" : "Не опубликован"}
                            />
                        </div>

                        <div className="tour-sites-block">
                            <span className="tour-sites-title">Доступен на сайтах</span>
                            <label className="tour-sites-option">
                                <input
                                    type="checkbox"
                                    checked={formState.showOnMainSite}
                                    onChange={(event) =>
                                        setFormState((prev) => ({ ...prev, showOnMainSite: event.target.checked }))
                                    }
                                />
                                Основной сайт (RU / EN / DE)
                            </label>
                            <label className="tour-sites-option">
                                <input
                                    type="checkbox"
                                    checked={formState.showOnUaSite}
                                    onChange={(event) =>
                                        setFormState((prev) => ({ ...prev, showOnUaSite: event.target.checked }))
                                    }
                                />
                                Второй сайт (RU / DE / UA)
                            </label>
                            {!formState.showOnMainSite && !formState.showOnUaSite ? (
                                <span className="tour-sites-hint">
                                    Не отмечен ни один сайт — тур не будет виден и продаваться на сайтах.
                                </span>
                            ) : null}
                        </div>

                        <div className="form-switch-row">
                            <Switch
                                checked={formState.apiSalesAllowed}
                                onChange={(checked) =>
                                    setFormState((prev) => ({ ...prev, apiSalesAllowed: checked }))
                                }
                                label={formState.apiSalesAllowed ? "API-продажи разрешены" : "API-продажи запрещены"}
                            />
                        </div>

                        <div className="form-switch-row">
                            <Switch
                                checked={formState.isSaleBlocked}
                                onChange={(checked) =>
                                    setFormState((prev) => ({ ...prev, isSaleBlocked: checked }))
                                }
                                label={formState.isSaleBlocked ? "Продажа заблокирована" : "Продажа открыта"}
                            />
                        </div>

                        <div className="form-switch-row">
                            <Switch
                                checked={formState.isActive}
                                onChange={(checked) =>
                                    setFormState((prev) => ({ ...prev, isActive: checked }))
                                }
                                label={formState.isActive ? "Тур активен" : "Тур выключен"}
                            />
                        </div>

                        {selectedId ? (
                            <>
                                <Card className="hall-gallery-card">
                                    <div className="editor-head">
                                        <div>
                                            <h3 className="card-title">Обложка тура</h3>
                                            <p className="card-text">
                                                Загрузка обложки доступна только для сохранённого тура (800 x 426 PNG, JPG).
                                            </p>
                                        </div>
                                    </div>

                                    <div className="hall-gallery-upload">
                                        <div
                                            className="hall-gallery-dropzone"
                                            role="button"
                                            tabIndex={0}
                                            onClick={() => coverInputRef.current?.click()}
                                            onKeyDown={(event) => {
                                                if (event.key === "Enter" || event.key === " ") {
                                                    event.preventDefault();
                                                    coverInputRef.current?.click();
                                                }
                                            }}
                                            onDragEnter={(event) => {
                                                event.preventDefault();
                                                event.stopPropagation();
                                            }}
                                            onDragOver={(event) => {
                                                event.preventDefault();
                                                event.stopPropagation();
                                                event.dataTransfer.dropEffect = "copy";
                                            }}
                                            onDrop={handleCoverDrop}
                                        >
                                            <input
                                                ref={coverInputRef}
                                                type="file"
                                                accept="image/*"
                                                className="hall-gallery-file-input"
                                                onChange={(event) => {
                                                    void handleUploadCover(event.target.files?.[0] ?? null);
                                                    event.currentTarget.value = "";
                                                }}
                                            />
                                            <span className="hall-gallery-dropzone-title">
                                                Выберите обложку
                                            </span>
                                            <span className="hall-gallery-dropzone-text">
                                                Перетащите файл сюда или нажмите для выбора.
                                            </span>
                                        </div>
                                    </div>

                                    {isUploadingCover ? (
                                        <div className="form-global-success">Загружаем обложку...</div>
                                    ) : null}

                                    {coverImage ? (
                                        <div className="hall-photos-grid">
                                            <div className="hall-photo-card">
                                                <img
                                                    src={coverImage}
                                                    alt="Tour cover"
                                                    className="hall-photo-preview"
                                                />
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="empty-block">
                                            <h3 className="empty-block-title">Обложка не загружена</h3>
                                            <p className="empty-block-text">
                                                После загрузки здесь появится превью.
                                            </p>
                                        </div>
                                    )}
                                </Card>

                                <Card className="hall-gallery-card">
                                    <div className="editor-head">
                                        <div>
                                            <h3 className="card-title">Фотогалерея тура</h3>
                                            <p className="card-text">
                                                Можно загрузить сразу несколько фотографий.
                                            </p>
                                        </div>
                                    </div>

                                    <div className="hall-gallery-upload">
                                        <div
                                            className="hall-gallery-dropzone"
                                            role="button"
                                            tabIndex={0}
                                            onClick={() => galleryInputRef.current?.click()}
                                            onKeyDown={(event) => {
                                                if (event.key === "Enter" || event.key === " ") {
                                                    event.preventDefault();
                                                    galleryInputRef.current?.click();
                                                }
                                            }}
                                            onDragEnter={(event) => {
                                                event.preventDefault();
                                                event.stopPropagation();
                                            }}
                                            onDragOver={(event) => {
                                                event.preventDefault();
                                                event.stopPropagation();
                                                event.dataTransfer.dropEffect = "copy";
                                            }}
                                            onDrop={handleGalleryDrop}
                                        >
                                            <input
                                                ref={galleryInputRef}
                                                type="file"
                                                multiple
                                                accept="image/*"
                                                className="hall-gallery-file-input"
                                                onChange={(event) => {
                                                    void handleUploadPhotos(event.target.files);
                                                    event.currentTarget.value = "";
                                                }}
                                            />
                                            <span className="hall-gallery-dropzone-title">
                                                Перетащите фото сюда или нажмите для выбора
                                            </span>
                                            <span className="hall-gallery-dropzone-text">
                                                Изображения загружаются по одному и затем появляются ниже.
                                            </span>
                                        </div>
                                    </div>

                                    {isUploadingPhotos ? (
                                        <div className="form-global-success">Загружаем фотографии...</div>
                                    ) : null}

                                    {photos.length > 0 ? (
                                        <div className="hall-photos-grid">
                                            {photos.map((photoUrl, index) => (
                                                <div key={`${photoUrl}-${index}`} className="hall-photo-card">
                                                    <img
                                                        src={photoUrl}
                                                        alt={`Tour photo ${index + 1}`}
                                                        className="hall-photo-preview"
                                                    />

                                                    <button
                                                        type="button"
                                                        className="hall-photo-delete"
                                                        onClick={() => {
                                                            void handleDeletePhoto(photoUrl);
                                                        }}
                                                        disabled={isDeletingPhoto}
                                                    >
                                                        ×
                                                    </button>

                                                    <div className="hall-photo-actions">
                                                        <Button
                                                            type="button"
                                                            variant="ghost"
                                                            onClick={() => {
                                                                void movePhoto(index, index - 1);
                                                            }}
                                                            disabled={index === 0}
                                                        >
                                                            ↑
                                                        </Button>

                                                        <Button
                                                            type="button"
                                                            variant="ghost"
                                                            onClick={() => {
                                                                void movePhoto(index, index + 1);
                                                            }}
                                                            disabled={index === photos.length - 1}
                                                        >
                                                            ↓
                                                        </Button>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="empty-block">
                                            <h3 className="empty-block-title">Фотографий пока нет</h3>
                                            <p className="empty-block-text">
                                                После загрузки здесь появятся превью и кнопки сортировки/удаления.
                                            </p>
                                        </div>
                                    )}
                                </Card>
                            </>
                        ) : null}

                        {formError ? <div className="form-global-error">{formError}</div> : null}
                        {successMessage ? <div className="form-global-success">{successMessage}</div> : null}

                        <div className="editor-actions">
                            <Button type="submit" disabled={isSaving}>
                                {isSaving
                                    ? "Сохраняем..."
                                    : selectedId
                                        ? "Сохранить изменения"
                                        : "Создать тур"}
                            </Button>
                        </div>
                    </form>
                </Card>
            </section>

            <Modal
                isOpen={isTourCityModalOpen}
                title={editingTourCityId ? "Редактировать город тура" : "Добавить город тура"}
                onClose={() => {
                    setIsTourCityModalOpen(false);
                    setEditingTourCityId(null);
                    setTourCityForm(initialTourCityForm);
                }}
            >
                <form className="editor-form" onSubmit={handleSubmitTourCity}>
                    {isGeoLoading ? (
                        <div className="form-global-success">Загружаем страны, города и залы...</div>
                    ) : null}

                    {isTourCityModalDataLoading ? (
                        <div>Загружаем данные для формы...</div>
                    ) : (
                        <>
                            <div className="form-grid form-grid-two-columns">
                                <Field label="Страна" htmlFor="tour-city-country">
                                    <Select
                                        id="tour-city-country"
                                        options={countryOptions}
                                        value={tourCityForm.countryId}
                                        onChange={(event) => {
                                            const nextCountryId = event.target.value;

                                            setTourCityForm((prev) => ({
                                                ...prev,
                                                countryId: nextCountryId,
                                                cityId: "",
                                                hallId: "",
                                            }));

                                            setCities([]);
                                            setHalls([]);

                                            if (nextCountryId) {
                                                void loadCitiesByCountry(nextCountryId);
                                            }
                                        }}
                                        placeholder={isCountriesLoading ? "Загружаем страны..." : "Выберите страну"}
                                        disabled={isCountriesLoading || isTourCityModalDataLoading}
                                    />
                                </Field>

                                <Field label="Город" htmlFor="tour-city-city">
                                    <Select
                                        id="tour-city-city"
                                        options={filteredCityOptions}
                                        value={tourCityForm.cityId}
                                        onChange={(event) => {
                                            const nextCityId = event.target.value;

                                            const selectedCityOption = filteredCityOptions.find(
                                                (item) => item.value === nextCityId,
                                            );

                                            const selectedCityLabel = selectedCityOption?.label ?? "";

                                            setTourCityForm((prev) => ({
                                                ...prev,
                                                cityId: nextCityId,
                                                hallId: "",
                                            }));

                                            setHalls([]);

                                            if (selectedCityLabel) {
                                                void loadHallsByCity(selectedCityLabel);
                                            }
                                        }}
                                        placeholder={
                                            !tourCityForm.countryId
                                                ? "Сначала выберите страну"
                                                : isCitiesLoading
                                                    ? "Загружаем города..."
                                                    : "Выберите город"
                                        }
                                        disabled={!tourCityForm.countryId || isCitiesLoading || isTourCityModalDataLoading}
                                    />
                                </Field>

                                <Field label="Зал" htmlFor="tour-city-hall">
                                    <Select
                                        id="tour-city-hall"
                                        options={hallSelectOptions}
                                        value={String(tourCityForm.hallId ?? "")}
                                        onChange={(event) =>
                                            setTourCityForm((prev) => ({
                                                ...prev,
                                                hallId: String(event.target.value),
                                            }))
                                        }
                                        placeholder={
                                            !tourCityForm.cityId
                                                ? "Сначала выберите город"
                                                : isHallsLoading
                                                    ? "Загружаем залы..."
                                                    : "Выберите зал"
                                        }
                                        disabled={!tourCityForm.cityId || isHallsLoading}
                                    />
                                </Field>

                                {isArtistProductionOrganizer ? (
                                    <Field label="ИД сеанса Artist Production" htmlFor="tour-city-partner-seance-id">
                                        <Input
                                            id="tour-city-partner-seance-id"
                                            type="number"
                                            value={tourCityForm.partnerSeanceId}
                                            onChange={(event) =>
                                                setTourCityForm((prev) => ({
                                                    ...prev,
                                                    partnerSeanceId: event.target.value,
                                                }))
                                            }
                                            placeholder="Например, 788"
                                        />
                                    </Field>
                                ) : null}

                                {isNasheDeOrganizer ? (
                                    <Field label="ИД мероприятия NasheDE" htmlFor="tour-city-nashe-performance-id">
                                        <Input
                                            id="tour-city-nashe-performance-id"
                                            type="number"
                                            value={tourCityForm.nashePerformanceId}
                                            onChange={(event) =>
                                                setTourCityForm((prev) => ({
                                                    ...prev,
                                                    nashePerformanceId: event.target.value,
                                                }))
                                            }
                                            placeholder="Например, 345"
                                        />
                                        <span style={{ fontSize: 12, color: "#8a8f98", display: "block", marginTop: 4 }}>
                                            Цены и свободные места подтягиваются из NasheDE, заказы резервируются и выкупаются через API NasheDE. Зал должен быть импортирован из этого же мероприятия (страница «Залы»).
                                        </span>
                                    </Field>
                                ) : null}

                                <Field label="Дата начала" htmlFor="tour-city-start-at">
                                    <Input
                                        id="tour-city-start-at"
                                        type="datetime-local"
                                        className="white-calendar-icon"
                                        value={tourCityForm.startAt}
                                        onChange={(event) =>
                                            setTourCityForm((prev) => ({
                                                ...prev,
                                                startAt: event.target.value,
                                            }))
                                        }
                                    />
                                </Field>

                                <Field label="Дата окончания" htmlFor="tour-city-end-at">
                                    <Input
                                        id="tour-city-end-at"
                                        type="datetime-local"
                                        className="white-calendar-icon"
                                        value={tourCityForm.endAt}
                                        onChange={(event) =>
                                            setTourCityForm((prev) => ({
                                                ...prev,
                                                endAt: event.target.value,
                                            }))
                                        }
                                    />
                                </Field>
                            </div>
                            <div className="form-grid form-grid-two-columns">
                                <div className="form-switch-row">
                                    <Switch
                                        checked={tourCityForm.soon}
                                        onChange={(checked) =>
                                            setTourCityForm((prev) => ({
                                                ...prev,
                                                soon: checked,
                                            }))
                                        }
                                        label={tourCityForm.soon ? "Скоро в продаже" : "Обычная продажа"}
                                    />
                                </div>
                                <div className="form-switch-row">
                                    <Switch
                                        checked={tourCityForm.isActive}
                                        onChange={(checked) =>
                                            setTourCityForm((prev) => ({
                                                ...prev,
                                                isActive: checked,
                                            }))
                                        }
                                        label={tourCityForm.isActive ? "Город тура активен" : "Город тура выключен"}
                                    />
                                </div>
                            </div>

                            <div className="editor-actions">
                                <Button type="submit" disabled={isSaving}>
                                    {isSaving ? "Сохраняем..." : editingTourCityId ? "Сохранить" : "Добавить"}
                                </Button>

                                <Button
                                    type="button"
                                    variant="ghost"
                                    onClick={() => {
                                        setIsTourCityModalOpen(false);
                                        setEditingTourCityId(null);
                                        setTourCityForm(initialTourCityForm);
                                    }}
                                >
                                    Отмена
                                </Button>
                            </div>
                        </>
                    )}
                </form>
            </Modal>

            <Modal
                isOpen={isDeleteTourCityModalOpen}
                title="Удалить город тура"
                onClose={() => {
                    setIsDeleteTourCityModalOpen(false);
                    setTourCityToDelete(null);
                }}
            >
                <div className="editor-form">
                    <p className="card-text">
                        Вы действительно хотите удалить город тура
                        {tourCityToDelete?.city?.name_ru ? ` «${tourCityToDelete.city.name_ru}»` : ""}?
                    </p>

                    <div className="editor-actions">
                        <Button type="button" onClick={() => void handleConfirmDeleteTourCity()}>
                            Удалить
                        </Button>
                        <Button
                            type="button"
                            variant="ghost"
                            onClick={() => {
                                setIsDeleteTourCityModalOpen(false);
                                setTourCityToDelete(null);
                            }}
                        >
                            Отмена
                        </Button>
                    </div>
                </div>
            </Modal>

            <HallPricesModal
                isOpen={isHallPricesModalOpen}
                scheme={selectedPricesScheme}
                hallName={selectedPricesHallName}
                tourCityId={tourCityForm.id}
                tourId={selectedId}
                tourOrganizerId={items.find((tour) => tour.id === selectedId)?.organizer_id ?? null}
                partnerSeanceId={selectedPricesPartnerSeanceId}
                onClose={() => {
                    setIsHallPricesModalOpen(false);
                    setSelectedPricesScheme(null);
                    setSelectedPricesHallName("");
                    setSelectedPricesPartnerSeanceId(null);
                }}
                onSave={(payload) => {
                    setSelectedPricesScheme(payload);
                }}
            />
        </div>
    );
};

export default ToursPage;
